import {
  type DashboardOrganizationSummary,
  type DashboardPendingInvite,
  getBillingBalanceAsUser,
  listIssueIntegrationProvidersAsUser,
  listOrganizationsAsUser,
  listPendingInvitesAsUser,
  listSourceControlProvidersAsUser,
} from "../dashboard-client";
import { hasGitHubAppInstallation } from "../utils/sourceControlUtils";

export type BootstrapStep =
  | "pending-invites"
  | "create-org"
  | "github"
  | "jira"
  | "payment"
  | "done";

export interface OnboardingBootstrapState {
  completed: Set<BootstrapStep>;
  organizations: DashboardOrganizationSummary[];
  pendingInvites: DashboardPendingInvite[];
  selectedOrgId: string | null;
  githubProviderId: string | null;
}

export async function loadOnboardingBootstrapState(
  preferredOrgId?: string | null,
): Promise<OnboardingBootstrapState> {
  const completed = new Set<BootstrapStep>();

  const [organizations, pendingInvites] = await Promise.all([
    listOrganizationsAsUser().then((res) => res.data.organizations),
    listPendingInvitesAsUser()
      .then((res) => res.data.invites)
      .catch(() => [] as DashboardPendingInvite[]),
  ]);

  let selectedOrgId: string | null = null;
  if (organizations.length > 0) {
    completed.add("pending-invites");
    const preferredOrg = preferredOrgId
      ? organizations.find((org) => org.organization_id === preferredOrgId)
      : null;
    if (preferredOrg) {
      selectedOrgId = preferredOrg.organization_id;
      completed.add("create-org");
    }
  } else if (pendingInvites.length === 0) {
    completed.add("pending-invites");
  }

  let githubProviderId: string | null = null;
  if (selectedOrgId) {
    await detectConnectedGitHub(completed).then((providerId) => {
      githubProviderId = providerId;
    });
    await detectConnectedJira(completed);
    await detectBillingSetup(completed, selectedOrgId);
  }

  return {
    completed,
    githubProviderId,
    organizations,
    pendingInvites,
    selectedOrgId,
  };
}

export function resolveInitialStep(
  requestedStep: string | null,
  completed: Set<BootstrapStep>,
  orderedSteps: readonly BootstrapStep[],
): BootstrapStep {
  const candidate =
    requestedStep === null
      ? null
      : (orderedSteps.find((step) => step === requestedStep) ?? null);
  if (
    candidate &&
    hasCompletedPrerequisites(candidate, completed, orderedSteps)
  ) {
    return candidate;
  }
  return (
    orderedSteps.find((step) => step !== "done" && !completed.has(step)) ??
    "done"
  );
}

function hasCompletedPrerequisites(
  step: BootstrapStep,
  completed: Set<BootstrapStep>,
  orderedSteps: readonly BootstrapStep[],
): boolean {
  const stepIndex = orderedSteps.indexOf(step);
  if (stepIndex === -1) {
    return false;
  }

  return orderedSteps
    .slice(0, stepIndex)
    .every((prerequisite) => completed.has(prerequisite));
}

async function detectConnectedGitHub(
  completed: Set<BootstrapStep>,
): Promise<string | null> {
  try {
    const bootstrapProvidersResult = await listSourceControlProvidersAsUser();
    const { providers } = bootstrapProvidersResult.data;
    const githubProvider = providers.find(
      (provider) =>
        provider.type === "github" &&
        hasGitHubAppInstallation(provider.metadata),
    );
    if (!githubProvider) {
      return null;
    }
    completed.add("github");
    return githubProvider.provider_id;
  } catch {
    return null;
  }
}

async function detectConnectedJira(
  completed: Set<BootstrapStep>,
): Promise<void> {
  try {
    const { data } = await listIssueIntegrationProvidersAsUser();
    if (data.providers.some((provider) => provider.type === "jira")) {
      completed.add("jira");
    }
  } catch {
    // Non-fatal
  }
}

async function detectBillingSetup(
  completed: Set<BootstrapStep>,
  selectedOrgId: string,
): Promise<void> {
  try {
    const { data: balanceData } = await getBillingBalanceAsUser({
      path: { organizationId: selectedOrgId },
    });
    if (balanceData.hasBillingProfile) {
      completed.add("payment");
    }
  } catch {
    // Non-fatal
  }
}
